import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  addDays as dateFnAddDay,
  addMonths as dateFnAddMonths,
  addWeeks as dateFnAddWeeks,
  endOfDay as dateFnEndOfDay,
  endOfMonth as dateFnEndOfMonth,
  endOfWeekWithOptions as dateFnEndOfWeek,
  startOfDay as dateFnStartOfDay,
  startOfMonth as dateFnStartOfMonth,
  startOfWeekWithOptions as dateFnStartOfWeek,
  getUnixTime as dateFnGetUnixTime,
  getMonth
} from 'date-fns/fp';

const MONTH_NAMES = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER'
];

interface DateTimeProps {
  // this is an ISO date string in the form of yyyy-MM-dd. It is already zoned to the marketplace
  readonly today: string;

  // the marketplace timezone
  readonly zone: string;

  // locale object from date-fns
  readonly locale?: Locale;
}

interface ComposeFromEpochProps {
  // the marketplace timezone
  readonly zone: string;

  // locale object from date-fns
  readonly locale?: Locale;
}

type ToDate = (props: DateTimeProps) => Date;
type DateTransform = (input: Date, locale?: Locale) => Date;

/**
 * Storm gives an ISO date (yyyy-MM-dd) in the marketplace timezone. The date-time library that we use (date-fns) does
 * not have a direct way to convert a zoned timestamp string into a zoned JS date. So that is why we need to do this operation
 * zoned timestamp-string -> utc js date -> zoned js-date
 * @param today
 * @param zone
 */
const parseIso = (today: string, zone: string) => {
  return utcToZonedTime(zonedTimeToUtc(today, zone), zone);
};

/**
 * Functional support for composing a chain of date time transforms. The initial date is assumed to be in the marketplace
 * timezone. All transformations will be for the current marketplace timezone. At the end, this function converts the
 * marketplace timezone back to UTC so that it can be used within Storm's calendar.
 * TODO: https://sim.amazon.com/issues/RCMRX-6042 For investigating alternative to functional composition
 * @param dateTimeProps the current date time
 * @param dateTimeToDate convert that turns a dateTime into a js date
 * @param fns transform functions to apply to the initial js date. Functions will be applied left to right
 */
const compose = (dateTimeProps: DateTimeProps, dateTimeToDate: ToDate, ...fns: DateTransform[]) => {
  const result = fns.reduce((prevDt: Date, currentValue: DateTransform) => {
    return currentValue(prevDt, dateTimeProps.locale);
  }, dateTimeToDate(dateTimeProps));

  return zonedTimeToUtc(result, dateTimeProps.zone);
};

/**
 * Applies transformations to the current time in the specified marketplace.
 * @param props input props for transformation
 * @param fns transform functions to apply to the initial js date. Functions will be applied left to right
 * @return date in marketplace timezone
 */
const composeInTZ = (props: ComposeFromEpochProps, ...fns: DateTransform[]) => {
  const nowInTZ = utcToZonedTime(Date.now(), props.zone);

  return fns.reduce((prevDt: Date, currentValue: DateTransform) => {
    return currentValue(prevDt, props.locale);
  }, nowInTZ);
};

/**
 * Coerces a date to a datetime at the beginning of the day (i.e. T00:00:00) in that marketplace
 * @param today Marketplace date in iso format yyyy-MM-DD
 * @param zone Marketplace timezone
 */
const startOfDay = ({ today, zone }: DateTimeProps) => {
  return dateFnStartOfDay(parseIso(today, zone));
};

/**
 * Coerces a date to a datetime at the end of the day (i.e. T23:59:59) in that marketplace
 * @param today Marketplace date in iso format yyyy-MM-DD
 * @param zone Marketplace timezone
 */
const endOfDay = ({ today, zone }: DateTimeProps) => {
  return dateFnEndOfDay(parseIso(today, zone));
};

/**
 * Adds N days to the current date. N can be positive or negative
 */
const addDays = dateFnAddDay;

/**
 * Truncates date to start of week. This needs a locale object because the definition of first day of the week various from
 * locale to locale. E.g. in US the week starts on Sunday. In France, the week starts on Monday. The more you know
 * @param date The current date in the marketplace timezone
 * @param locale Locale object from date-fns
 */
const startOfWeek = (date: Date, locale?: Locale) => {
  return dateFnStartOfWeek({ locale }, date);
};

/**
 * Truncates date to end of week. This needs a locale object because the definition of first day of the week various from
 * locale to locale. E.g. in US the week starts on Sunday. In France, the week starts on Monday. The more you know
 * @param date The current date in the marketplace timezone
 * @param locale Locale object from date-fns
 */
const endOfWeek = (date: Date, locale?: Locale) => {
  return dateFnEndOfWeek({ locale }, date);
};

/**
 * Adds N weeks to the current date. N can be positive or negative
 */
const addWeeks = dateFnAddWeeks;

/**
 * Truncates to beginning of month
 */
const startOfMonth = dateFnStartOfMonth;

/**
 * Truncates to end of month
 */
const endOfMonth = dateFnEndOfMonth;

/**
 * Adds N months to current date. N can be positive or negative
 */
const addMonths = dateFnAddMonths;

const getUnixTime = dateFnGetUnixTime;

const getUnixTimeInMillis = (date: Date) => getUnixTime(date) * 1000;

/**
 * Gets the month number given a string
 * @param month the month string, e.g. March, APRIL, june, AuGuSt
 * Firefox needs date with a year for date object.
 */
const getMonthNumber = (month: string) => {
  const monthNumber = getMonth(new Date(`${month} 1 1970`)) + 1;
  return `${monthNumber}`.padStart(2, '0');
};

const getMonthName = (monthNumber: number) => {
  return MONTH_NAMES[monthNumber];
};

export default {
  compose,
  composeInTZ,
  startOfDay,
  endOfDay,
  addDays,
  startOfWeek,
  endOfWeek,
  addWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  getUnixTime,
  getUnixTimeInMillis,
  getMonthNumber,
  getMonthName
};
